import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FileText, Menu, Palette, Globe2, ArrowRightLeft, Activity, Link2, ImageOff, Sparkles } from 'lucide-react';
import { AiModelsPanel } from './AiModelsPanel';
import { toast } from 'sonner';
import { usePermissions } from '../../../context/PermissionContext';
import { websiteApi } from './api';
import { PagesList } from './PagesList';
import { PageBuilder } from './PageBuilder';
import { NavigationManager } from './NavigationManager';
import { DesignPanel, GlobalsPanel, RedirectsPanel, SeoDashboard } from './SitePanels';
import { PageHealthAlerts } from './PageHealthAlerts';
import { LinkCheckPanel } from './LinkCheckPanel';
import { StockImageAudit } from './StockImageAudit';
import { AiCreatePageDialog } from './AiAssistant';

const VIEWS = [
  { id: 'pages', label: 'Pages', icon: FileText }, { id: 'seo', label: 'SEO Health', icon: Activity }, { id: 'links', label: 'Link Check', icon: Link2 }, { id: 'images', label: 'Image Audit', icon: ImageOff }, { id: 'navigation', label: 'Navigation', icon: Menu },
  { id: 'design', label: 'Design System', icon: Palette }, { id: 'globals', label: 'Global Elements', icon: Globe2 }, { id: 'redirects', label: 'Redirects', icon: ArrowRightLeft }, { id: 'ai-models', label: 'AI Models', icon: Sparkles },
];

// Website Builder — upgrades the former "Pages & SEO" tab. Permissions: website (falls back to settings).
const WebsiteTab = ({ token }) => {
  const api = useMemo(() => websiteApi(token), [token]);
  const { hasPermission } = usePermissions();
  const can = (lvl) => hasPermission('website', lvl) || hasPermission('settings', lvl);
  const canEdit = can('edit'), canPublish = can('full');
  const [view, setView] = useState('pages');
  const [registry, setRegistry] = useState(null);
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [aiCreate, setAiCreate] = useState(false);

  const refresh = useCallback(() => api.pages().then((p) => { setPages(p); window.dispatchEvent(new Event('website-pages-changed')); }).catch(() => toast.error('Could not load pages')), [api]);
  useEffect(() => { api.registry().then(setRegistry).catch(() => toast.error('Could not load builder registry')); refresh(); }, [api, refresh]);
  const [focus, setFocus] = useState(null);
  const openPage = useCallback((slug, f = null) => { setFocus(f); setEditing(slug); }, []);
  useEffect(() => {
    const slug = sessionStorage.getItem('website_open_slug');
    if (!slug) return;
    const f = sessionStorage.getItem('website_open_focus');
    sessionStorage.removeItem('website_open_slug'); sessionStorage.removeItem('website_open_focus');
    openPage(slug, f ? JSON.parse(f) : null);
  }, [openPage]);

  if (editing) return <PageBuilder api={api} slug={editing} registry={registry} pages={pages} canPublish={canPublish} initialFocus={focus} onBack={() => { setEditing(null); setFocus(null); refresh(); }} onSaved={refresh} />;

  return (
    <div data-testid="website-tab">
      <div className="flex flex-wrap items-center gap-1 border-b border-neutral-200 mb-6">
        {VIEWS.map((v) => <button key={v.id} onClick={() => setView(v.id)} data-testid={`website-view-${v.id}`} className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 -mb-px ${view === v.id ? 'border-neutral-900 text-neutral-900 font-medium' : 'border-transparent text-neutral-500 hover:text-neutral-900'}`}><v.icon className="w-4 h-4" />{v.label}</button>)}
      </div>
      {view === 'pages' && <div className="mb-6"><PageHealthAlerts api={api} onOpenPage={openPage} /></div>}
      {view === 'links' && <LinkCheckPanel api={api} onOpenPage={openPage} />}
      {view === 'images' && <StockImageAudit token={token} />}
      {view === 'pages' && <PagesList api={api} pages={pages} templates={registry?.templates || []} canEdit={canEdit} canDelete={canPublish} onOpen={openPage} onRefresh={refresh} onAiCreate={() => setAiCreate(true)} />}
      {view === 'seo' && <SeoDashboard api={api} onOpen={openPage} />}
      {view === 'navigation' && <NavigationManager api={api} pages={pages} canEdit={canEdit} />}
      {view === 'design' && <DesignPanel api={api} canEdit={canPublish} />}
      {view === 'globals' && <GlobalsPanel api={api} canEdit={canEdit} />}
      {view === 'redirects' && <RedirectsPanel api={api} canEdit={canEdit} />}
      {view === 'ai-models' && <AiModelsPanel api={api} canEdit={canPublish} />}
      <AiCreatePageDialog api={api} open={aiCreate} onClose={() => setAiCreate(false)} templates={registry?.templates || []} onCreated={(page) => { setAiCreate(false); refresh(); setEditing(page.slug); }} />
    </div>
  );
};

export default WebsiteTab;
